import { convertStringToFloatWithTwoDecimals } from '@/Utils/FnUtils';
import {
  buildMissionConditionText,
  combineDateAndTime,
  EMissionType,
  MISSION_DESCRIPTION_DEFAULT,
  MISSION_TITLE_DEFAULT,
} from '../constants';
import type {
  CreateMissionFormType,
  MissionRewardLevel,
  PostMissionParams,
  PutMissionParams,
} from '../types';

type MissionRewardLevelFormFields = Pick<
  CreateMissionFormType,
  | 'objective01'
  | 'objective02'
  | 'objective03'
  | 'reward01'
  | 'reward02'
  | 'reward03'
>;

export const buildRewardLevels = (
  data: MissionRewardLevelFormFields,
): MissionRewardLevel[] => {
  const levels = [
    { objective: data.objective01, reward: data.reward01 },
    { objective: data.objective02, reward: data.reward02 },
    { objective: data.objective03, reward: data.reward03 },
  ];

  return levels
    .filter(level => level.objective && level.reward)
    .map(level => {
      const conditionValue = Number(level.objective);

      return {
        conditionText: buildMissionConditionText(conditionValue),
        conditionValue,
        rewardValue: Number(
          convertStringToFloatWithTwoDecimals(level.reward as string),
        ),
      };
    });
};

export const mapCreateMissionFormToPayload = (
  data: CreateMissionFormType,
  managerId: string,
): PostMissionParams => {
  const isCityMission = data.targetAudience === EMissionType.CITY;
  const idsDocument =
    !isCityMission && data.deliverymanCsv ? data.deliverymanCsv : undefined;

  return {
    startDate: combineDateAndTime(data.startDate, data.startTime),
    endDate: combineDateAndTime(data.endDate, data.endTime),
    type: data.targetAudience as EMissionType,
    cityIds: isCityMission ? data.cityIds || [] : [],
    deliverymanIds:
      !isCityMission && !data.deliverymanCsv ? data.deliverymanIds || [] : [],
    ...(idsDocument ? { idsDocument } : {}),
    name: data.missionName,
    budget: Number(convertStringToFloatWithTwoDecimals(data.budget)),
    rewardLevels: buildRewardLevels(data),
    title: data.missionTitle?.trim() || MISSION_TITLE_DEFAULT,
    description: data.missionDescription?.trim() || MISSION_DESCRIPTION_DEFAULT,
    managerId,
  };
};

export const mapCreateMissionFormToUpdatePayload = (
  data: CreateMissionFormType,
  missionId: string,
): PutMissionParams => ({
  missionId,
  startDate: combineDateAndTime(data.startDate, data.startTime),
  endDate: combineDateAndTime(data.endDate, data.endTime),
  name: data.missionName,
  budget: Number(convertStringToFloatWithTwoDecimals(data.budget)),
  rewardLevels: buildRewardLevels(data),
  title: data.missionTitle?.trim() || MISSION_TITLE_DEFAULT,
  description: data.missionDescription?.trim() || MISSION_DESCRIPTION_DEFAULT,
});
